import { BookingWizard } from "../components/BookingWizard/BookingWizard";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  BookingWizardProvider,
  BookingState,
  useBookingWizard,
} from "../contexts/BookingWizardContext";
import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { Spin, Result, Button } from "antd";
import { message } from "antd";
import { useCompany } from "../hooks/useCompany";
import { useCompanyDetails } from "../hooks/useCompanyDetails";

export const BookingWizardPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { selectedCompany } = useCompany();
  const {
    details: companyDetails,
    loading: companyLoading,
    error,
  } = useCompanyDetails(selectedCompany);
  const [initialData, setInitialData] = useState<BookingState | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(true);

  // Check if we're returning from MyProducts page
  const fromProductPage = location.state?.fromProductPage;
  const returnToStep = location.state?.returnToStep;

  // Check if company is approved
  const isCompanyApproved =
    companyDetails &&
    companyDetails.status && // Check that status exists
    companyDetails.status !== "unapproved" &&
    companyDetails.status !== "under_review";

  // Check if there was an error loading company details
  const hasCompanyError = error && !companyDetails;

  useEffect(() => {
    const checkCompanyStatus = async () => {
      setLoading(true);

      // Only proceed if company details are loaded
      if (companyLoading) {
        return;
      }

      // If there was an error loading company details, don't proceed
      if (hasCompanyError) {
        setLoading(false);
        return;
      }

      // If we have company details and company is not approved, don't load booking data
      if (companyDetails && !isCompanyApproved) {
        setLoading(false);
        return;
      }

      // If we're editing an existing booking, fetch its data
      if (id) {
        try {
          const { data, error } = await supabase
            .from("bookings")
            .select(
              `
              *,
              transport_type:booking_transport_types(id, name, abbreviation),
              booking_type:bookings_type(id, name),
              booking_incoterm:bookings_incoterm(id, name),
              supplier:booking_supplier(id, name, email, phone, wechat, country, province, city, address),
              products:booking_product(id, name, hs_code, ean, gross_weight, cbm, value, pieces, cartons)
            `
            )
            .eq("id", id)
            .single();

          if (error) {
            console.error("Error fetching booking:", error);
            message.error("Failed to load booking");
            return;
          }

          if (data) {
            // Redirect to view page if booking is submitted or approved
            if (
              data.status === "submitted" ||
              data.status === "approved" ||
              (data.status !== "concept" && data.status !== "rejected")
            ) {
              navigate(`/bookings/${id}/view`, { replace: true });
              return;
            }

            // Determine which step we should return to
            // Default is step 0, but if we're returning from MyProducts page, use the returnToStep value
            const currentStep =
              fromProductPage && typeof returnToStep === "number"
                ? returnToStep
                : 0;

            console.log("Setting booking step to:", currentStep);

            // Transform the data to match BookingState structure
            const transformedData: BookingState = {
              id: data.id,
              status: data.status, // Keep the original status, including "rejected"
              reference: data.reference,
              rejection_reason: data.rejection_reason,
              transport_type: data.transport_type?.id,
              booking_type: data.booking_type?.id,
              booking_incoterm: data.booking_incoterm?.id,
              supplier_id: data.supplier?.id,
              products: data.products,
              commercial_invoice_file: data.commercial_invoice_file,
              origin_pickup_date: data.origin_pickup_date,
              company_id: data.company_id,
              currentStep: currentStep, // Use the determined step
              isComplete: false,
            };
            setInitialData(transformedData);
          }
        } catch (error) {
          console.error("Error in fetchBooking:", error);
          message.error("An error occurred while loading the booking");
        }
      }

      setLoading(false);
    };

    checkCompanyStatus();
  }, [
    id,
    companyDetails,
    isCompanyApproved,
    companyLoading,
    navigate,
    fromProductPage,
    returnToStep,
  ]);

  // Show loading spinner if either company details are loading or we're loading booking data
  if (companyLoading || loading) {
    return <Spin size="large" />;
  }

  // If there was an error loading company details, show an error message
  if (hasCompanyError) {
    return (
      <Result
        status="error"
        title="Company Not Found"
        subTitle="The selected company could not be found. It may have been deleted or you may not have access to it."
        extra={[
          <Button
            key="company-info"
            type="primary"
            onClick={() => navigate("/company-info")}
          >
            Go to Company Info
          </Button>,
          <Button key="back" onClick={() => navigate("/bookings")}>
            Back to Bookings
          </Button>,
        ]}
      />
    );
  }

  // If company is not approved or status is missing, show a message and prevent access
  if (!isCompanyApproved) {
    const status = companyDetails?.status || "missing";
    let message = "";

    if (status === "unapproved") {
      message =
        "Your company is not approved yet. You cannot create new bookings until your company is approved. Please complete your company information.";
    } else if (status === "under_review") {
      message =
        "Your company is under review. You cannot create new bookings until the review is complete.";
    } else {
      message =
        "Your company status is not set. Please complete your company information to create bookings.";
    }

    return (
      <Result
        status="warning"
        title="Company Not Approved"
        subTitle={message}
        extra={[
          <Button
            key="company-info"
            type="primary"
            onClick={() => navigate("/company-info")}
          >
            Go to Company Info
          </Button>,
          <Button key="back" onClick={() => navigate("/bookings")}>
            Back to Bookings
          </Button>,
        ]}
      />
    );
  }

  return (
    <BookingWizardProvider initialData={initialData}>
      <ForceStepInitialization
        step={
          fromProductPage && typeof returnToStep === "number"
            ? returnToStep
            : undefined
        }
      />
      <BookingWizard onClose={() => navigate("/bookings")} />
    </BookingWizardProvider>
  );
};

// Component to force the step initialization
const ForceStepInitialization = ({ step }: { step?: number }) => {
  const { setCurrentStep, state } = useBookingWizard();
  const location = useLocation();
  const forceStep = location.state?.forceStep;

  useEffect(() => {
    // Priority: forceStep from location state (if available), otherwise use prop
    const targetStep = typeof forceStep === "number" ? forceStep : step;

    if (typeof targetStep === "number") {
      console.log("ForceStepInitialization - Will set step to:", targetStep);

      // Use a sequence of timeouts with increasing delays to ensure the step is set correctly
      const setStepWithTimeout = (delay: number, attempt: number = 1) => {
        setTimeout(() => {
          // Check if the current step is already correct
          if (state.currentStep !== targetStep) {
            console.log(
              `ForceStepInitialization - Setting step${
                attempt > 1 ? " AGAIN" : " NOW"
              } to:`,
              targetStep
            );
            setCurrentStep(targetStep);

            // If we haven't tried too many times yet, schedule another attempt
            if (attempt < 5) {
              // Use exponential backoff for retries with increasing delays
              setStepWithTimeout(delay * 1.5, attempt + 1);
            }
          }
        }, delay);
      };

      // Start the sequence of attempts
      setStepWithTimeout(100);
    }
  }, [step, forceStep, setCurrentStep, state.currentStep]);

  return null;
};
